import {
  ConnectionDetails,
  HandleErrorInput,
  convertConnectionToDetails,
  handleError,
  mergeConnectionDetails as merge,
} from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import { Import, PreviousImportConnectionInput, SearchPreviousImportsResponsePreviousImportConnectionArgs } from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { SearchPreviousImportsDocument } from '@/api/queries';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';

// Global Service Variables
const mergeKey = 'searchPreviousImports.previousImportConnection';
const onError = (res: HandleErrorInput): void => handleError(res, { notification: { title: 'Search Previous Imports' } });

// Service Function Signature
type ServiceMethodPayload = Omit<PreviousImportConnectionInput, 'first' | 'after'>;
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<Import>>;
}

// SearchPreviousImports Service
export const { searchPreviousImports, refetchPreviousImport, useSearchPreviousImports } = ((): {
  searchPreviousImports: ServiceMethod;
  refetchPreviousImport: ServiceMethod;
  useSearchPreviousImports: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchPreviousImportsDocument, {
    onError,
  }).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<Import>> => {
        const args: SearchPreviousImportsResponsePreviousImportConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            importId: payload?.importId,
            providerId: payload?.providerId,
          },
        };
        const opts = { merge: options?.merge ? mergeKey : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty(mergeKey, res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchPreviousImports: fetch,
    refetchPreviousImport: refetch,
    useSearchPreviousImports: useService,
  };
})();
